import type { QuoteRequestForm, ShippingFeeSettings } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { estimateQuoteWithShipping, PENDING_ADDRESS_HINT } from '@/entities/quote';
import { formatCount, formatKrw } from '@/shared/lib';
import { EyebrowLabel } from '@/shared/ui';
import { EstimateShippingBreakdown } from './estimate-shipping-breakdown';

export interface EstimateSummaryProps {
  readonly tiers: QuoteRequestForm['priceTiers'];
  readonly minOrderQty: number;
  /** 배송지 수량 합계 — 카드 수량이 하나라도 정수가 아니면 null. */
  readonly totalQuantity: number | null;
  /** 카드 순서대로 우편번호(고르기 전이면 ''). */
  readonly postalCodes: readonly string[];
  /** 요청 폼 조회의 배송비 설정(정적 기본값이 아니라 지금 설정, spec §6-1). */
  readonly shippingFeeSettings: ShippingFeeSettings;
}

const CELL_TERM_CLASS = 'text-xs text-muted-foreground';

/**
 * 임시 견적(견적 설계 §5-5, SUPER-86 spec §6-1) — 상품 금액(적용 단가 × 총 수량) + 배송지별 배송비 = 합계(부가세 포함)와 공급가액·부가세.
 * 내 견적 상세의 확정 견적 금액과 같은 모양 — 굵은 윗선, 오른쪽 큰 합계, 아래 상품 금액·배송비 / 공급가액·부가세 네 칸.
 * 주소를 아직 고르지 않은 배송지는 기본 배송비로 세고 안내를 붙인다. 계산할 수 없으면(최소 수량 미만·빈 수량·구간 없음) 안내만 둔다.
 */
export const EstimateSummary = ({
  tiers,
  minOrderQty,
  totalQuantity,
  postalCodes,
  shippingFeeSettings,
}: EstimateSummaryProps): ReactElement => {
  const belowMinimum = totalQuantity !== null && totalQuantity < minOrderQty;
  // 최소 주문 수량 미만이면 더 낮은 단가 구간이 있어도 계산하지 않는다 — 서버가 INVALID_QUANTITY 로 거절하는 수량이다.
  const estimate =
    totalQuantity === null || belowMinimum
      ? null
      : estimateQuoteWithShipping(tiers, totalQuantity, postalCodes, shippingFeeSettings);

  let notice: ReactElement | null = null;

  if (belowMinimum) {
    notice = (
      <p className="text-sm text-destructive">
        최소 주문 수량은 {formatCount(minOrderQty)}개입니다.
      </p>
    );
  } else if (estimate === null && totalQuantity !== null) {
    notice = (
      <p className="text-sm text-muted-foreground">
        단가 구간 정보가 없어 임시 견적을 계산할 수 없습니다.
      </p>
    );
  } else if (estimate === null) {
    notice = (
      <p className="text-sm text-muted-foreground">수량을 입력하면 임시 견적을 계산합니다.</p>
    );
  }

  return (
    // 수량·주소를 바꿀 때마다 합계가 바뀐다 — 화면낭독기에도 조용히 알린다.
    <section
      aria-label="임시 견적"
      aria-live="polite"
      className="flex flex-col gap-4 border-t border-foreground pt-5"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <EyebrowLabel>Estimate</EyebrowLabel>
          <h2 className="text-sm font-semibold">임시 견적</h2>
          {estimate !== null && totalQuantity !== null ? (
            <p className="text-xs text-muted-foreground tabular-nums">
              {formatCount(totalQuantity)}개 기준 · 부가세 포함
            </p>
          ) : null}
        </div>
        {estimate !== null ? (
          <p className="text-[2rem] leading-none font-bold tracking-[-0.02em] tabular-nums">
            {formatKrw(estimate.totalAmount)}
          </p>
        ) : null}
      </div>

      {estimate !== null ? (
        <dl className="grid grid-cols-2 border-y border-foreground/10 text-sm [&>div:nth-child(-n+2)]:border-b [&>div:nth-child(-n+2)]:border-foreground/10 [&>div:nth-child(odd)]:border-r [&>div:nth-child(odd)]:border-foreground/10">
          <div className="flex flex-col gap-1 py-3 pr-3">
            <dt className={CELL_TERM_CLASS}>상품 금액</dt>
            <dd className="flex flex-col gap-0.5 tabular-nums">
              {formatKrw(estimate.productAmount)}
              <span className="text-xs text-muted-foreground">
                개당 {formatKrw(estimate.tier.unitPrice)} · {formatCount(estimate.tier.minQty)}개
                이상 구간
              </span>
            </dd>
          </div>
          <div className="flex flex-col gap-1 py-3 pl-3">
            <dt className={CELL_TERM_CLASS}>
              배송비 · 배송지 {formatCount(estimate.shipping.lines.length)}곳
            </dt>
            <dd className="flex flex-col gap-1.5 tabular-nums">
              {formatKrw(estimate.shipping.shippingAmount)}
              <EstimateShippingBreakdown lines={estimate.shipping.lines} />
              {estimate.shipping.hasPendingAddress ? (
                <span className="text-xs text-muted-foreground">{PENDING_ADDRESS_HINT}</span>
              ) : null}
            </dd>
          </div>
          <div className="flex flex-col gap-1 py-3 pr-3">
            <dt className={CELL_TERM_CLASS}>공급가액</dt>
            <dd className="tabular-nums">{formatKrw(estimate.supplyAmount)}</dd>
          </div>
          <div className="flex flex-col gap-1 py-3 pl-3">
            <dt className={CELL_TERM_CLASS}>부가세</dt>
            <dd className="tabular-nums">{formatKrw(estimate.vatAmount)}</dd>
          </div>
        </dl>
      ) : (
        <div className="border-y border-foreground/10 py-4">{notice}</div>
      )}
    </section>
  );
};
